import { ClipboardType } from '../../types';
import { ReadParamsMap, WriteParamsMap } from '../clipboard-delegate';

export class SlideLegacyClipboard {
    private static instance: SlideLegacyClipboard;

    public static getInstance(): SlideLegacyClipboard {
        if (!SlideLegacyClipboard.instance) {
            SlideLegacyClipboard.instance = new SlideLegacyClipboard();
        }
        return SlideLegacyClipboard.instance;
    }

    public async write(args: Pick<WriteParamsMap, 'adapter' | 'legacy'>): Promise<void> {
        const { event } = args.adapter || {};
        const { arr } = args.legacy || {};

        if (!event) {
            return new Promise((resolve) => {
                this.writeByNavigator([new ClipboardItem(args.adapter.asyncClipboardItemConf as any)])
                    .then(() => {
                        resolve();
                    })
                    .catch((error) => {
                        window.log.error('Failed to write to clipboard: ', error);
                        resolve();
                    });
            });
        }

        if (event) {
            return new Promise((resolve) => {
                try {
                    arr?.forEach(({ format, data }) => {
                        this.writeByClipboardEvent({
                            event,
                            format,
                            data,
                        });
                    });
                    resolve();
                } catch (error) {
                    window.log.error('Failed to write to clipboard: ', error);
                    resolve();
                }
            });
        }

        return Promise.resolve();
    }

    public async read(params: Pick<ReadParamsMap, 'adapter' | 'legacy'>): Promise<unknown> {
        const { event, readFile } = params.adapter;

        if (!event) {
            return new Promise((resolve) => {
                this.readByNavigator()
                    .then((res) => {
                        resolve(res);
                    })
                    .catch((error) => {
                        window.log.error('Failed to read from clipboard: ', error);
                        resolve(undefined);
                    });
            });
        }

        if (readFile) {
            return new Promise((resolve) => {
                try {
                    const data = this.readFileByClipboardEvent(event);
                    resolve(data);
                } catch (error) {
                    window.log.error('Failed to read from clipboard: ', error);
                    resolve(undefined);
                }
            });
        }

        return new Promise((resolve) => {
            try {
                const data = this.readByClipboardEvent(event);
                resolve(data);
            } catch (error) {
                window.log.error('Failed to read from clipboard: ', error);
                resolve(undefined);
            }
        });
    }

    private async writeByNavigator(data: ClipboardItems) {
        return navigator.clipboard.write(data);
    }

    private async readByNavigator() {
        return navigator.clipboard.read();
    }

    private writeByClipboardEvent({ event, format, data }: { event: ClipboardEvent; format: string; data: string }) {
        const clipboardData = event.clipboardData as DataTransfer;
        clipboardData.setData(format, data);
    }

    private readByClipboardEvent(event: ClipboardEvent) {
        const clipboardData = event.clipboardData as DataTransfer;
        const html = clipboardData.getData(ClipboardType.HTML);
        const text = clipboardData.getData(ClipboardType.TEXT);
        return { html, text };
    }

    private readFileByClipboardEvent(event: ClipboardEvent) {
        const { items, files } = event.clipboardData as DataTransfer;
        return Array.from(files) || Array.from(items).map(item => item.getAsFile());
    }
}
